import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { ConfigProvider, theme as antdTheme } from "antd";
import { useAuth } from "./AuthContext";
import apiService from "../utils/api";

const DEFAULT_THEME = {
  token: {
    colorPrimary: "#00875A",       // Keka-style teal
    colorSuccess: "#00875A",       // Teal
    colorInfo: "#0065FF",          // Blue
    colorWarning: "#FF991F",       // Amber
    colorError: "#DE350B",         // Red
    colorTextBase: "#172B4D",      // Navy text
    colorTextSecondary: "#5E6C84", // Medium gray
    colorBgBase: "#FFFFFF",        // White base
    colorBgLayout: "#F4F5F7",     // Warm gray layout
    borderRadius: 6,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 13,
    wireframe: false,
    colorBorder: "#DFE1E6",
    colorBorderSecondary: "#EBECF0",
    controlHeight: 36,
    lineHeight: 1.5,
  },
  components: {
    Button: {
      controlHeight: 36,
      borderRadius: 6,
      algorithm: true,
      defaultShadow: "none",
      primaryShadow: "0 2px 4px rgba(0, 135, 90, 0.2)",
      fontWeight: 500,
      paddingInline: 16,
    },
    Input: {
      controlHeight: 36,
      borderRadius: 6,
      colorBorder: "#DFE1E6",
      hoverBorderColor: "#00875A",
      activeBorderColor: "#00875A",
      activeShadow: "0 0 0 2px rgba(0, 135, 90, 0.1)",
    },
    Select: {
      controlHeight: 36,
      borderRadius: 6,
      colorBorder: "#DFE1E6",
      hoverBorderColor: "#00875A",
      activeBorderColor: "#00875A",
      activeShadow: "0 0 0 2px rgba(0, 135, 90, 0.1)",
    },
    Card: {
      borderRadiusLG: 8,
      boxShadowTertiary: "0 1px 2px rgba(9, 30, 66, 0.08)",
      paddingLG: 20,
      colorBorderSecondary: "#EBECF0",
    },
    Table: {
      borderRadiusLG: 8,
      headerBg: "#FAFBFC",
      headerColor: "#5E6C84",
      headerBorderRadius: 8,
      rowHoverBg: "#FAFBFC",
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
      headerSplitColor: "transparent",
    },
    Menu: {
      itemBorderRadius: 6,
      itemHeight: 40,
      itemMarginInline: 8,
      activeBarWidth: 0,
      itemSelectedBg: "rgba(255, 255, 255, 0.10)",
      itemSelectedColor: "#FFFFFF",
      itemColor: "rgba(255, 255, 255, 0.70)",
      itemHoverBg: "rgba(255, 255, 255, 0.06)",
      itemHoverColor: "#FFFFFF",
      subMenuItemBg: "transparent",
      darkItemBg: "transparent",
      darkSubMenuItemBg: "transparent",
      darkItemSelectedBg: "rgba(255, 255, 255, 0.10)",
      darkItemHoverBg: "rgba(255, 255, 255, 0.06)",
    },
    Layout: {
      bodyBg: "#F4F5F7",
      headerBg: "#FFFFFF",
      siderBg: "#1B2559",
      headerHeight: 56,
    },
    Modal: {
      borderRadiusLG: 12,
      paddingContentHorizontalLG: 24,
    },
    Tabs: {
      itemColor: "#5E6C84",
      itemSelectedColor: "#00875A",
      itemHoverColor: "#00875A",
      inkBarColor: "#00875A",
    },
    Tag: {
      borderRadiusSM: 4,
    },
    Typography: {
      fontFamilyCode: "'Source Code Pro', monospace",
    },
    Breadcrumb: {
      separatorColor: "#C1C7D0",
      linkColor: "#5E6C84",
      lastItemColor: "#172B4D",
    },
  },
  algorithm: antdTheme.defaultAlgorithm,
};

interface TenantThemeContextType {
  themeConfig: any;
  tenantConfig: Record<string, any> | null;
  refreshTheme: () => void;
}

const TenantThemeContext = createContext<TenantThemeContextType | undefined>(undefined);

export const useTenantTheme = () => {
  const context = useContext(TenantThemeContext);
  if (context === undefined) {
    throw new Error('useTenantTheme must be used within a TenantThemeProvider');
  }
  return context;
};

interface TenantThemeProviderProps {
  children: ReactNode;
}

export const TenantThemeProvider: React.FC<TenantThemeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [tenantConfig, setTenantConfig] = useState<Record<string, any> | null>(null);

  const fetchTheme = React.useCallback(async () => {
    if (!user) {
      setTenantConfig(null);
      return;
    }
    try {
      const res = await apiService.getTenantConfig();
      if (res?.success && res?.data && Object.keys(res.data).length > 0) {
        setTenantConfig(res.data);
      } else {
        setTenantConfig(null);
      }
    } catch {
      setTenantConfig(null);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);

  const themeConfig = React.useMemo(() => {
    const c = tenantConfig || {};

    const tokenOverrides = {
      ...(c.primary_color && { colorPrimary: c.primary_color }),
      ...(c.secondary_color && { colorSuccess: c.secondary_color }),
      ...(c.accent_color && { colorInfo: c.accent_color }),
      ...(c.border_radius && { borderRadius: parseInt(c.border_radius, 10) || 6 }),
      ...(c.font_family && c.font_family !== "default" && {
        fontFamily: `'${c.font_family}', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif`,
      }),
    };

    return {
      ...DEFAULT_THEME,
      token: {
        ...DEFAULT_THEME.token,
        ...tokenOverrides,
      },
      components: {
        ...DEFAULT_THEME.components,
        Button: {
          ...DEFAULT_THEME.components.Button,
          ...(c.primary_color && { primaryShadow: `0 2px 4px ${c.primary_color}33` })
        }
      }
    };
  }, [tenantConfig]);

  // Set CSS variables for Sidebar, Header, Footer, Logo background
  React.useEffect(() => {
    const root = document.documentElement;
    if (tenantConfig?.primary_color) {
      const primary = tenantConfig.primary_color;
      root.style.setProperty("--tenant-primary", primary);
      root.style.setProperty("--tenant-sidebar-bg", `linear-gradient(180deg, ${primary} 0%, ${primary}CC 100%)`);
      root.style.setProperty("--tenant-logo-bg", primary);
      root.style.setProperty("--tenant-header-border", primary);
      root.style.setProperty("--tenant-footer-bg", primary);
      root.style.setProperty("--tenant-footer-text", "#ffffff");
      root.style.setProperty("--tenant-footer-border", "rgba(0,0,0,0.15)");
    } else {
      root.style.removeProperty("--tenant-primary");
      root.style.removeProperty("--tenant-sidebar-bg");
      root.style.removeProperty("--tenant-logo-bg");
      root.style.removeProperty("--tenant-header-border");
      root.style.removeProperty("--tenant-footer-bg");
      root.style.removeProperty("--tenant-footer-text");
      root.style.removeProperty("--tenant-footer-border");
    }
  }, [tenantConfig]);

  const value = { themeConfig, tenantConfig, refreshTheme: fetchTheme };

  return (
    <TenantThemeContext.Provider value={value}>
      <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>
    </TenantThemeContext.Provider>
  );
};
